import { zodResolver } from "@hookform/resolvers/zod";
import { useIsFetching } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { translateDomainError } from "#app/i18n/translateApiError";
import { showErrorToast } from "#app/notifications";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import {
  SidePanel,
  SidePanelContent,
  SidePanelFooter,
  SidePanelHeader,
} from "#components/molecules/SidePanel/SidePanel";
import { useApplyLayoutNesting } from "#modules/layouts/view/useApplyLayoutNesting";
import { useLayoutNesting } from "#modules/layouts/view/useLayoutNesting";
import { useRunLayoutNesting } from "#modules/layouts/view/useRunLayoutNesting";
import { useUpdateLayout } from "#modules/layouts/view/useUpdateLayout";
import {
  buildNestingInputsDefaultValues,
  NestingInputsFormInput,
  NestingInputsFormSchema,
  NestingInputsFormValues,
} from "#modules/layoutsV2/domain/nestingInputsFormSchema";
import {
  getLayoutScrapBillingRate,
  toScrapBillingRatePayload,
} from "#modules/layoutsV2/domain/scrapBillingRate";
import { NestingInputs } from "#modules/layoutsV2/view/NestingInputs";
import { NestingPlan } from "#modules/layoutsV2/view/NestingPlan";
import { DiscardChangesModal } from "#modules/optimizations/view/DiscardChangesModal";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { useUpdateLayoutMaterial } from "#modules/quoteRequestLines/views/DetailsPanel/Material/useUpdateLayoutMaterial";
import { Flex } from "#shared/view/components/Flex";
import {
  computeMaterialPrice,
  FormatterMaterialParameter,
  isDomainError,
  isNestingRunApplied,
  JobStatusSchema,
  MaterialGradeType,
  PartTypeSchema,
  NO_OPTIMIZED_MATERIAL_COSTS,
  getNestedLineQuantity,
} from "shared";

type Props = {
  opened: boolean;
  onClose: () => void;
  materialGrade: MaterialGradeType;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
  quoteRequestLine: Omit<QuoteRequestLineDto, "layout"> & {
    layout: NonNullable<QuoteRequestLineDto["layout"]>;
  };
};

const IN_PROGRESS_STATUSES = new Set<string>([
  JobStatusSchema.enum.PENDING,
  JobStatusSchema.enum.STANDBY,
  JobStatusSchema.enum.PROCESSING,
]);

const parseMillimeters = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
};

export const LayoutSidePanel = ({
  opened,
  onClose,
  quoteRequestLine,
  materialGrade,
  partDimensionsWithOverthickness,
}: Props) => {
  const { t } = useTranslation();
  const { layout, part } = quoteRequestLine;
  const savedBilledShare = getLayoutScrapBillingRate(layout);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<NestingInputsFormInput, unknown, NestingInputsFormValues>({
    defaultValues: buildNestingInputsDefaultValues(layout),
    resolver: zodResolver(NestingInputsFormSchema),
  });
  const [billedShare, setBilledShare] = useState(String(savedBilledShare));
  const [committedBilledShare, setCommittedBilledShare] = useState<number | null>(null);
  const [isDiscardModalOpened, setIsDiscardModalOpened] = useState(false);

  const resetFormsToLayout = useEffectEvent(() => {
    reset(buildNestingInputsDefaultValues(layout));
    setBilledShare(String(savedBilledShare));
    setCommittedBilledShare(null);
  });

  useEffect(() => {
    if (opened) {
      resetFormsToLayout();
    }
  }, [opened]);

  useEffect(() => {
    resetFormsToLayout();
  }, [layout.cutWidth, layout.skeletonWidth, savedBilledShare]);

  const { nestingRun, isSuccess: isNestingRunFetched } = useLayoutNesting(layout.id);
  const { updateLayout, isPending: isUpdatingLayout } = useUpdateLayout(quoteRequestLine.id);
  const { updateLayout: updateBilledShare, isPending: isUpdatingBilledShare } = useUpdateLayout(
    quoteRequestLine.id,
  );
  const simulatedBilledShare = committedBilledShare ?? savedBilledShare;
  const hasEditedBilledShare = simulatedBilledShare !== savedBilledShare;
  const hasUnsavedChanges = isDirty || hasEditedBilledShare;
  const isRefetchingLine = useIsFetching(quoteRequestLineQuery({ id: quoteRequestLine.id })) > 0;
  const isMaterialCostLoading = isUpdatingBilledShare || isRefetchingLine;
  const { runLayoutNesting, isPending: isRunningNesting } = useRunLayoutNesting(layout.id, {
    onError: (error) =>
      showErrorToast({
        title: isDomainError(error) ? translateDomainError(error) : t("layout.errors.nestingError"),
      }),
  });
  const { updateLayoutMaterial, isPending: isUpdatingMaterial } = useUpdateLayoutMaterial({
    materialLayout: layout.materialLayout,
    materialGrade,
    quoteRequestLineId: quoteRequestLine.id,
  });
  const { applyLayoutNesting, isPending: isApplyingNesting } = useApplyLayoutNesting(
    layout.id,
    quoteRequestLine.id,
    {
      onSuccess: onClose,
      onError: (error) =>
        showErrorToast({
          title: isDomainError(error)
            ? translateDomainError(error)
            : t("layout.errors.nestingError"),
        }),
    },
  );

  const isNestingInProgress = nestingRun ? IN_PROGRESS_STATUSES.has(nestingRun.status) : false;
  const isCalculating = isUpdatingMaterial || isUpdatingLayout || isRunningNesting;
  const isLoading = isNestingInProgress || isCalculating;
  const isNestingProcessed = nestingRun?.status === JobStatusSchema.enum.PROCESSED;
  const canApplyNesting = isNestingProcessed && !isNestingRunApplied(layout, nestingRun);
  const canSave =
    (canApplyNesting || hasEditedBilledShare) &&
    !isLoading &&
    !isApplyingNesting &&
    !isUpdatingBilledShare &&
    !isDirty;

  const { partMaterialCost } = computeMaterialPrice(
    {
      quoteRequestLine: {
        ...quoteRequestLine,
        layout: { ...layout, ...toScrapBillingRatePayload(simulatedBilledShare) },
      },
      optimizedMaterialCosts: NO_OPTIMIZED_MATERIAL_COSTS,
    },
    computationOptions,
  );
  const nestingItems = [
    {
      id: quoteRequestLine.id,
      part,
      quantity: getNestedLineQuantity(quoteRequestLine),
      unitMaterialCost: partMaterialCost,
    },
  ];

  const materialName =
    quoteRequestLine.materialName ||
    (quoteRequestLine.materialGrade
      ? t(`qrl.materialGrades.${quoteRequestLine.materialGrade}`)
      : "-");
  const subtitle = [t(`part.type.${part.type ?? PartTypeSchema.enum.OTHER}`), materialName].join(
    " · ",
  );

  const updateLayoutThenRunNesting = (
    { cutWidth, skeletonWidth }: NestingInputsFormValues,
    submittedInputs: NestingInputsFormInput,
  ) => {
    const payload = {
      cutWidth,
      skeletonWidth,
      ...toScrapBillingRatePayload(simulatedBilledShare),
      useNesting: true,
      quoteRequestLineId: quoteRequestLine.id,
    };
    updateLayout(
      { id: layout.id, payload },
      {
        onSuccess: () => {
          reset(submittedInputs);
          runLayoutNesting();
        },
      },
    );
  };

  const commitBilledShare = () => {
    const billedShareRate = parseMillimeters(billedShare);
    if (billedShareRate !== null) {
      setCommittedBilledShare(billedShareRate);
    }
  };

  const syncBilledShare = (onSuccess: () => void) => {
    if (!hasEditedBilledShare) {
      onSuccess();
      return;
    }
    updateBilledShare(
      {
        id: layout.id,
        payload: {
          ...toScrapBillingRatePayload(simulatedBilledShare),
          quoteRequestLineId: quoteRequestLine.id,
        },
      },
      { onSuccess },
    );
  };

  const save = () =>
    syncBilledShare(() => {
      if (canApplyNesting) {
        applyLayoutNesting();
        return;
      }
      onClose();
    });

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpened(true);
      return;
    }
    onClose();
  };

  const leaveWithoutSaving = () => {
    setIsDiscardModalOpened(false);
    onClose();
  };

  const calculate = (values: NestingInputsFormValues) => {
    const submittedInputs = getValues();
    if (values.material) {
      updateLayoutMaterial(values.material, {
        onSuccess: () => updateLayoutThenRunNesting(values, submittedInputs),
      });
      return;
    }
    updateLayoutThenRunNesting(values, submittedInputs);
  };

  const hasAutoRunForCurrentOpening = useRef(false);
  const runInitialNesting = useEffectEvent(() => handleSubmit(calculate)());
  const hasNoNestingRun = isNestingRunFetched && !nestingRun;

  useEffect(() => {
    if (!opened) {
      hasAutoRunForCurrentOpening.current = false;
      return;
    }
    if (hasNoNestingRun && !hasAutoRunForCurrentOpening.current) {
      hasAutoRunForCurrentOpening.current = true;
      void runInitialNesting();
    }
  }, [opened, hasNoNestingRun]);

  return (
    <SidePanel opened={opened} onClose={requestClose}>
      <SidePanelHeader
        title={t("layout.sidePanel.title")}
        subtitle={subtitle}
        onClose={requestClose}
        onBack={requestClose}
        closeText={t("common.close")}
        backText={t("layout.sidePanel.back")}
      />
      <SidePanelContent>
        <Flex direction="column" gap={spacing[8]} h="100%">
          <NestingInputs
            materialLayout={layout.materialLayout}
            partType={part.type}
            partSubType={part.subType}
            materialGrade={materialGrade}
            materialName={materialName}
            quoteRequestLineId={quoteRequestLine.id}
            partDimensionsWithOverthickness={partDimensionsWithOverthickness}
            control={control}
            isCalculating={isCalculating}
            isNestingInProgress={isNestingInProgress}
            isDirty={isDirty}
            onCalculate={handleSubmit(calculate)}
          />

          <NestingPlan
            items={nestingItems}
            nestingRun={nestingRun}
            isLoading={isLoading}
            billedShare={billedShare}
            onBilledShareChange={setBilledShare}
            onBilledShareBlur={commitBilledShare}
            isMaterialCostLoading={isMaterialCostLoading}
          />
        </Flex>
      </SidePanelContent>
      <SidePanelFooter>
        <Flex align="center" justify="flex-end" gap={spacing[3]} w="100%">
          <Button size="lg" variant="secondary" onClick={requestClose}>
            {t("layout.sidePanel.cancel")}
          </Button>
          <Button
            size="lg"
            loading={isApplyingNesting || isUpdatingBilledShare}
            disabled={!canSave}
            onClick={save}
          >
            {t("layout.sidePanel.save")}
          </Button>
        </Flex>
      </SidePanelFooter>
      <DiscardChangesModal
        opened={isDiscardModalOpened}
        onLeave={leaveWithoutSaving}
        onKeepEditing={() => setIsDiscardModalOpened(false)}
      />
    </SidePanel>
  );
};
